import { FC, useMemo } from "react"
import { FieldProps } from "@rjsf/core"
import { TableColumn, TextInput, resolveSchema } from "resha"
import { ExclusiveChoiceDropdown } from "dash-styled"
import { isJSONSchema7 } from "universal-schema/lib"
import { ArrayAsTableBase } from "./ArrayAsTableBase"

interface ValidationRule {
    type: string
    pattern: string
}

export const ValidationRulesField: FC<
    FieldProps<ValidationRule[] | undefined>
> = fieldProps => {
    const {
        formContext,
        schema,
        formData = [],
        onChange,
        uiSchema
    } = fieldProps

    const rootSchema = formContext.ref.current?.state.schema
    const ruleSchema =
        rootSchema && isJSONSchema7(schema) && isJSONSchema7(schema.items)
            ? resolveSchema(schema.items, rootSchema)
            : undefined
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const ruleTypes = isJSONSchema7(ruleSchema?.properties?.type)
        ? (ruleSchema?.properties?.type?.enum as string[])
        : []

    const defaultValidationRule = useMemo<ValidationRule>(
        () => ({
            type: ruleTypes[0],
            pattern: ""
        }),
        [ruleTypes]
    )

    const addButtonLabel = uiSchema["ui:options"]?.addButtonLabel as
        | string
        | undefined

    const handleChangePattern = (index: number) => (value: string) => {
        const newValue = [...formData]
        newValue[index] = {
            ...newValue[index],
            pattern: value
        }
        onChange(newValue)
    }
    const handleChangeType = (ruleIndex: number) => (typeIndex: number) => {
        const newValue = [...formData]
        newValue[ruleIndex] = {
            ...newValue[ruleIndex],
            type: ruleTypes[typeIndex]
        }
        onChange(newValue)
    }

    const columns: TableColumn<ValidationRule>[] = [
        {
            className: () => "w-4/12 bg-white border-b-rhino-300",
            renderHeader: () => null,
            headerClassName: "hidden",
            render: (value, isChecked, rowIndex) => {
                const typeIndex = ruleTypes.findIndex(
                    type => type === value.type
                )
                return (
                    <ExclusiveChoiceDropdown
                        choices={ruleTypes}
                        selectedIndex={typeIndex}
                        className="ml-1"
                        fitContentWidth={false}
                        size="sm"
                        onClick={handleChangeType(rowIndex)}
                    />
                )
            }
        },
        {
            className: () => "pl-2 bg-white border-b-rhino-300",
            renderHeader: () => null,
            headerClassName: "hidden",
            render: (value, isChecked, rowIndex) => {
                return (
                    <TextInput
                        value={value.pattern}
                        enableCopyTextButton={false}
                        enableMarkdown={false}
                        onValueChange={handleChangePattern(rowIndex)}
                        inputClassName="rounded-lg bg-transparent text-sm leading-5 py-2 px-3"
                        className="rounded-lg border border-rhino-300"
                    />
                )
            }
        }
    ]

    return (
        <div>
            <ArrayAsTableBase<ValidationRule>
                values={formData}
                internalColumns={columns}
                onChange={onChange}
                defaultValue={defaultValidationRule}
                addButtonLabel={addButtonLabel}
            />
        </div>
    )
}
